/**
 * Pure helpers for the order "job card" detail page (`/orders/:storeId/:orderId`). Kept separate from
 * the component so the order→batch→press join is unit-tested.
 *
 * The card answers "where is this whole order in production?" by joining three things the server
 * already exposes — with NO new endpoint:
 *   1. `GET /orders/:storeId/:orderId` → the order header, its lines, and the DTF-production rollup.
 *   2. each line's denormalized `batchId` (set at BATCHED, kept through PRINTED) → the gang sheet.
 *   3. `GET /batches/:batchId` → that sheet's status + `screenPrintWork[]` (per-placement `pressedAt`).
 *
 * "Production complete" is DERIVED, not a stored flag (bagging is a downstream/shipping step): a line
 * only reaches workflow PRINTED through the batch mark-printed gate, which requires every screen-print
 * placement pressed — so `production.status === 'PRINTED'` transitively means every placement ON THE
 * LINE'S OWN SHEET is printed and pressed. No persistence, no Shopify write.
 *
 * ⚠️ That is NOT the same as shippable, and the gap is customer-facing (decision D4 / §8.25). A REISSUE
 * batch reprints a garment without ever repointing the source line's `batchId`, so the ledger keeps
 * reading PRINTED while the replacement garment is still on the press.
 *
 * ⚠️ THE SHIP ANSWER IS {@link shipVerdict}, built on {@link garmentOutcome}. Every ship-facing
 * affordance keys on it — the strip at the top of the page and the {@link orderHeadline} badge — and
 * never on `production.status`, which the server derives with a hole this cannot have. {@link
 * JobCard.productionComplete} is the same verdict as a boolean; it has no renderer of its own today
 * and exists as the tested invariant, so do NOT read this list as "productionComplete is the gate".
 */
import type { components } from '@/lib/api/types.gen';
import { isRealSize, isYouthSize, normalizeSizeName } from './inventory-lib';

export type OrderDetail = components['schemas']['OrderDetail'];
export type OrderSummary = components['schemas']['OrderSummary'];
export type OrderLineView = components['schemas']['OrderLineView'];
export type OrderProduction = components['schemas']['OrderProduction'];
export type OrderProductionStatus = OrderProduction['status'];
export type BatchDetail = components['schemas']['BatchDetail'];
export type BatchSummary = components['schemas']['BatchSummary'];
export type BatchItemSummary = components['schemas']['BatchItemSummary'];
export type BatchStatus = BatchSummary['status'];
export type ScreenPrintWorkItem = NonNullable<BatchSummary['screenPrintWork']>[number];
export type ReprintRef = components['schemas']['ReprintRef'];
/** The two arms, named. A BATCHED reprint is already on film; a QUEUED one has no sheet yet, so it
 *  carries no batchId/seq/status and a reader must narrow on `kind` before reaching for them. */
export type BatchedReprintRef = components['schemas']['BatchedReprintRef'];
export type QueuedReprintRef = components['schemas']['QueuedReprintRef'];

/**
 * Group outstanding reprints (from `GET /orders/:store/:id/reprints`) by the lineId they reprint. Each
 * ref lists every lineId OF THIS ORDER the reissue batch touches, so one reissue batch can appear under
 * several garment rows. Rollo Phase 4: drives the per-garment "reprint outstanding — batch #N" marker.
 */
export function reprintsByLine(reprints: readonly ReprintRef[]): Map<string, ReprintRef[]> {
  const out = new Map<string, ReprintRef[]>();
  for (const ref of reprints) {
    for (const lineId of ref.lineIds) {
      const arr = out.get(lineId);
      if (arr) arr.push(ref);
      else out.set(lineId, [ref]);
    }
  }
  return out;
}

/** One screen-print placement on a garment, with its check-off state. */
export type ScreenPrintPlacement = {
  location: string;
  designName?: string;
  quantity: number;
  pressed: boolean;
  /** Completion time — set when every owed copy is pressed (production-records R0 display). */
  pressedAt?: string;
};

/** One decoration placement on a garment — a DTF transfer OR a screen print — with its press state.
 *  DTF presses are checked off via POST /batches/:id/press; screen print via the SP worklist mark. */
export type GarmentPlacement = {
  kind: 'dtf' | 'screen-print';
  location: string;
  designName?: string;
  pressed: boolean;
  /** Completion time — set when every owed copy is pressed. ⚠️ it is NOT the first copy's press time
   *  (per-copy times don't exist in the data yet — production-records R1 adds them). */
  pressedAt?: string;
  /** DTF only: pressed from a printed transfer pulled off the shelf (not this batch's sheet). */
  fromStock?: boolean;
};

/**
 * The accessible name of a press check-off — **the only control on this card that spends a blank.**
 *
 * ⚠️ It used to be `` `${location} ${kind} pressed` `` and nothing else, which names a PLACEMENT and not
 * a GARMENT. Measured on `fsp-core-prod`: of the 321 orders with lines on a sheet, **146 (45%) render two
 * or more checkboxes carrying an identical accessible name** — 403 lines — and the worst live order,
 * #L-6998, renders **seven** boxes all called "back DTF pressed" plus six called "front screen print
 * pressed". Completing the last placement of a garment runs `settleLine`, which consumes its blank, so an
 * ambiguous name on this control is a mislabel affordance in front of a spend: the same failure the pile
 * dividers, the YOUTH marker and PR-4's size chip all exist to reduce, on the tap that actually pays.
 * It bites a screen reader, a voice-control user, and any automation that selects by name.
 *
 * ⚠️ **The variant is not decoration here.** 99 lines across 42 orders share a title with a sibling on the
 * same order (worst: 4 lines, one title), so the title alone still leaves boxes indistinguishable — and it
 * is precisely the same-product-different-size case where a mis-tap draws the wrong blank. `Default Title`
 * is Shopify's placeholder for a single-variant product and is dropped: it is noise, not identity.
 *
 * Follows the house convention rather than inventing one — `/press` already qualifies its own check-offs
 * (`Mark ${method} ${location} of ${orderName} …`). It qualifies by ORDER because a worklist row's
 * ambiguity is which order; this card fixes the order in the URL, so its ambiguity is which GARMENT.
 *
 * ⚠️ The `${location} ${kind} pressed` tail is preserved deliberately, and it is not laziness: ten test
 * selectors key off that substring precisely because a money review put them there. Appending identity in
 * FRONT keeps every one of them valid — screen readers and voice control both disambiguate on the leading
 * words — so this pass adds protection instead of rewriting it. The new property (sibling garments get
 * DISTINCT names) is asserted separately, because no existing test could have failed on it.
 */
export function pressCheckboxLabel(
  g: Pick<JobCardGarment, 'title' | 'variantTitle'>,
  p: Pick<GarmentPlacement, 'kind' | 'location'>,
): string {
  const variant = g.variantTitle && g.variantTitle !== 'Default Title' ? g.variantTitle : undefined;
  const identity = variant ? `${g.title}, ${variant}` : g.title;
  return `${identity} — ${p.location} ${p.kind === 'dtf' ? 'DTF' : 'screen print'} pressed`;
}

/**
 * The who/when stamp for a garment's CURRENT recorded state (production-records R0): off-system
 * print, shelf pull, cap produce, or ship-only pack. Checks class+state directly (not the stage
 * label) so a FULFILLED line still shows how its production was recorded — the review case.
 */
export function garmentStamp(
  g: Pick<
    JobCardGarment,
    | 'workflowState'
    | 'productionClass'
    | 'nonDtfWorkflowState'
    | 'fulfillWorkflowState'
    | 'producedAt'
    | 'producedBy'
    | 'packedAt'
    | 'packedBy'
    | 'alreadyPrintedAt'
    | 'alreadyPrintedBy'
    | 'pulledFromStockAt'
    | 'pulledFromStockBy'
  >,
): string | null {
  if (g.workflowState === 'ALREADY_PRINTED') {
    return formatStamp(g.alreadyPrintedAt, g.alreadyPrintedBy);
  }
  if (g.workflowState === 'PULLED_FROM_STOCK') {
    return formatStamp(g.pulledFromStockAt, g.pulledFromStockBy);
  }
  if (g.productionClass === 'NON_DTF' && g.nonDtfWorkflowState === 'PRODUCED') {
    return formatStamp(g.producedAt, g.producedBy);
  }
  if (g.productionClass === 'FULFILLMENT_ONLY' && g.fulfillWorkflowState === 'PACKED') {
    return formatStamp(g.packedAt, g.packedBy);
  }
  return null;
}

/**
 * "who · when" line under a stage badge (production-records R0). Both parts optional — the data
 * predates some stamps. Returns null when neither exists so callers can skip the row entirely.
 */
export function formatStamp(at?: string, by?: string): string | null {
  if (!at && !by) return null;
  const when = at
    ? new Date(at).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : null;
  if (by && when) return `${by} · ${when}`;
  return by ?? when;
}

/**
 * What the row prints in its Qty cell. A CANCELLED line owes nothing, so it gets an em-dash rather
 * than a number — see {@link JobCardGarment.quantity} for why a cancelled line can still be carrying
 * a non-zero `currentQty` straight from Shopify.
 *
 * ⚠️ This is the ORDERED count ("how many did the customer buy"), which is deliberately a different
 * question from the header's piece count ("how many garments does this shop owe production on", i.e.
 * `production.pieces`, which excludes cancelled AND non-produced lines server-side). The two are not
 * expected to reconcile by addition, which is why they are worded differently on screen.
 *
 * `prefix` is the phone layout's `×`, which belongs to the NUMBER and not to the em-dash — the two
 * call sites would otherwise render "×—" on a cancelled line (seen in a browser; jsdom cannot show
 * it). Taking the prefix here rather than at the call site keeps one rule: a future change to what a
 * cancelled row prints lands on the phone and the desktop together, instead of on whichever one the
 * next person remembers.
 */
export function garmentQtyLabel(
  g: Pick<JobCardGarment, 'quantity' | 'fulfillmentState'>,
  prefix = '',
): string {
  if (g.fulfillmentState === 'CANCELLED') return '—';
  return `${prefix}${g.quantity}`;
}

/** The forward Ship-only pack, offered on the card, with what it will actually cost. */
export type ShipOnlyPack = {
  /** Units the server will draw. Identical to `packConsumeQty` server-side, which is
   *  `max(0, currentQty ?? unfulfilledQty)` and 0 on a cancelled line — i.e. exactly
   *  {@link JobCardGarment.quantity}. Named here so the sentence and the draw cannot drift. */
  qty: number;
  /** Whether a stock movement actually happens. False when the product is unmapped OR qty is 0 —
   *  the server draws only `if (itemSlug && qty > 0)`, and BOTH halves are live shapes. */
  movesStock: boolean;
  /** A complete sentence, true whether or not anything is drawn. */
  cost: string;
  /** The order has already shipped — this records a pack after the fact. Worth saying out loud. */
  retro: boolean;
};

/**
 * Should this row offer the forward "Mark packed", and what does it cost? (PR-5b)
 *
 * ⚠️ **The gate mirrors the SERVER's, condition for condition**, because a button that renders and
 * then 409s is worse than no button: `markLinePacked`'s DDB condition requires `workflowState = OPEN`
 * (a lane line's DTF workflow always is; a line reclassified mid-flight is not), and the class check
 * is the route's own refusal. CANCELLED is excluded to match the caps forward button — a cancelled
 * line owes no pack and no stock, so offering the action is pure noise.
 *
 * ⚠️ **`cost` is derived, never asserted.** Measured 2026-08-10 across all 512 orders: **20 of the 21
 * recorded packs drew nothing**, because exactly one ship-only product has ever been mapped to a shelf
 * item. A button claiming "draws 1 from the shelf" would therefore be a false statement on almost
 * every row it appears on — the same defect the approved mockup shipped on the undo side.
 *
 * This is the door for the **8 shipped-but-unpacked lines that no other surface can reach**: the
 * Ship-only worklist lists UNFULFILLED lines only, so a line packed after its order shipped has no
 * row there and never will.
 */
export function shipOnlyPack(
  g: Pick<
    JobCardGarment,
    | 'productionClass'
    | 'fulfillWorkflowState'
    | 'workflowState'
    | 'fulfillmentState'
    | 'quantity'
    | 'stockItemSlug'
  >,
): ShipOnlyPack | undefined {
  if (g.productionClass !== 'FULFILLMENT_ONLY') return undefined;
  if (g.fulfillWorkflowState === 'PACKED') return undefined;
  if (g.workflowState !== 'OPEN') return undefined;
  if (g.fulfillmentState === 'CANCELLED') return undefined;

  const qty = g.quantity;
  const item = g.stockItemSlug;
  const movesStock = Boolean(item) && qty > 0;
  const retro = g.fulfillmentState === 'FULFILLED';
  return {
    qty,
    movesStock,
    cost: movesStock
      ? `Draws ${qty} × ${item} from the shelf.`
      : item
        ? 'Nothing is drawn — this line is down to zero units.'
        : 'Nothing is drawn — this product isn’t mapped to anything on the shelf.',
    retro,
  };
}

/**
 * What the forward "Mark printed off-system" records, in visible text under the button (PR-6).
 *
 * ⚠️ It claims NOTHING about shipping, and that is measured rather than cautious. `deriveOrderProduction`
 * tests `fulfillmentState === 'FULFILLED'` and `continue`s **before** it reaches its ALREADY_PRINTED
 * short-circuit, so on the only population this button is offered to (see
 * {@link canMarkPrintedOffSystem}) the mark moves `status`, `pieces`, `outstandingPieces` and every
 * lane counter by exactly zero. `garmentStage` short-circuits on FULFILLED too, so even the row's badge
 * is unchanged. The whole ship-claim hazard the plan warns about (§2.7/§5.4 — a mark dropping `open`
 * and flipping a rollup to PRINTED) lives in the UNSHIPPED arm, which this gate excludes.
 *
 * So the honest sentence is the narrow one: a record is written, and nothing else moves.
 */
export const OFF_SYSTEM_MARK_COST =
  'Records the production only — no blank is drawn, and the order’s status doesn’t change.';

/**
 * Should this row offer the forward "Mark printed off-system"? (PR-6)
 *
 * This is the door for the **259 measured lines whose production was never recorded and which no
 * worklist can reach**: they shipped, so `queuePartitionFor` returns `undefined` for them and `/orders`
 * (a sparse GSI on the UNFULFILLED partition) has no row for them and never will. It is also what
 * makes the "Still needs printing" undo one row up REVERSIBLE — all 22 live ALREADY_PRINTED lines land
 * back in this gate after an undo, which is why {@link shippedUndo}'s off-system arm can now name the
 * way back instead of calling itself one-way.
 *
 * ⚠️ **`productionClass !== 'NON_DTF' && !== 'FULFILLMENT_ONLY'`, NOT `=== 'DTF_APPAREL'`** — the
 * plan's §5.4 default says the latter and it is wrong. `productionClass` is re-derived from the catalog
 * on every ingest and is simply ABSENT on 237 live lines (all FULFILLED, all created ≤ 2026-07-14, so
 * nothing re-ingests them). `order-production.ts` states the rule this mirrors — *"An absent
 * productionClass ⇒ DTF_APPAREL"* — and the server route refuses by the same negative form. Gating
 * positively would have hidden the button on **220 of the 259** rows it exists for: 84% of the
 * population, every one of them a Tee or a Hoodie.
 *
 * ⚠️ **`fulfillmentState === 'FULFILLED'` is a DELIBERATE NARROWING of the plan, not an omission — and
 * it is the clause that carries the whole safety argument.** The server would accept the mark on the 83
 * UNSHIPPED lines too, and there one tap flips the order's rollup toward PRINTED, drops the line out of
 * the print queue *and* out of blank demand, and no alarm, lane or sweep detects it: a garment that never
 * gets printed and never gets bought, silently, with the customer as the detector. That population has a
 * purpose-built door on `/orders` (whose entire page is that decision, with selection and counts), and
 * `garmentIdleNote` links this row straight to it. Two doors to the dangerous half for zero extra reach
 * is a worse card. **Widening this clause later is not a no-op** — it brings the whole ship-claim hazard
 * back and makes an inventory invalidation mandatory.
 *
 * ⚠️ **`mappingStatus === 'MAPPED'` mirrors the SERVER's own definition of "unrecorded", and that — not
 * safety — is its justification.** An earlier draft of this comment claimed MAPPED "carries the whole
 * safety argument"; an adversarial review showed that is false, and the clause above is what does. Once
 * a line is FULFILLED no rollup can move, so admitting an EXCLUDED decal would manufacture no ship claim
 * — it would only write a wrong audit row. What MAPPED actually buys is parity: `isUnrecordedFulfilledLine`
 * (`rest-api/src/routes/orders.ts`) refuses a non-MAPPED line with the comment *"excluded / unmapped —
 * was never producible here"*, and it is what feeds the **`N not recorded` badge** on
 * `/orders/completed`. This gate is that predicate ∩ OPEN ∩ no-batch, so the button appears exactly on
 * the rows that badge counts, and marking one clears the count it was flagged by. Relaxing MAPPED would
 * put a button on rows nothing flags. (The pair the plan feared, DTF_APPAREL × EXCLUDED, has **0 rows**;
 * the 15 real decals/keychains/snapbacks carry an ABSENT class, which is why this clause and not the
 * class check is what excludes them. A `CUSTOMER_SUPPLIED` shipped garment is UNMAPPED by construction
 * and is therefore foreclosed here — accepted, because the badge does not count it either.)
 *
 * `workflowState === 'OPEN'` is `markLinePrintedOffSystem`'s own DDB condition. `!batchId` earns its
 * place twice: it keeps this branch from shadowing a live press checklist, and the mark does NOT
 * `REMOVE batchId`, so a line carrying one could later be scope-matched by `resetLineOpen`'s
 * `batchId = :b` condition and dragged back to OPEN *and re-queued* — an off-system record silently
 * undone by a batch operation. 0 such rows exist (measured: no OPEN line in prod carries a batchId), so
 * this is the guard that keeps it that way rather than one that fires today.
 *
 * Reconciles exactly with the plan's §0 count, which is worth stating because the two numbers differ:
 * 314 OPEN+FULFILLED lines − 38 cap/ship-only = the plan's **276**, of which 15 are EXCLUDED decals and
 * 2 UNMAPPED. 276 − 17 = the **259** this gate serves.
 */
export function canMarkPrintedOffSystem(
  g: Pick<
    JobCardGarment,
    'productionClass' | 'workflowState' | 'mappingStatus' | 'fulfillmentState' | 'batchId'
  >,
): boolean {
  if (g.productionClass === 'NON_DTF' || g.productionClass === 'FULFILLMENT_ONLY') return false;
  if (g.workflowState !== 'OPEN') return false;
  if (g.mappingStatus !== 'MAPPED') return false;
  if (g.fulfillmentState !== 'FULFILLED') return false;
  if (g.batchId) return false;
  return true;
}

/**
 * An undo the row is offering on a line the shop can no longer put back — what it costs, in words.
 *
 * A discriminated object, NEVER a nullable string: the caller has to narrow before it can render, so
 * "no warning" and "a warning that happens to be empty" cannot be confused. That distinction is the
 * whole reason a shirt once shipped with its back omitted (`truthiness-gate-drops-a-falsy-reason`).
 */
export type ShippedUndo = {
  /** Which of the four undos this is. DERIVED, never passed in — see {@link shippedUndo}. */
  kind: 'off-system' | 'produce' | 'pack' | 'stock-pick';
  /** Why the line can no longer come back. A complete sentence. */
  lead: string;
  /** What happens in a stock ledger — a complete sentence, and true when the answer is "nothing". */
  cost: string;
  /** Whether {@link cost} describes a real ledger movement. Drives the tint and the confirm's wording;
   *  two of the three undos spend, one does not, and flattening that would make the loud one ordinary. */
  movesStock: boolean;
  /** The worklist it leaves, and the fact that nothing in the app can put it back. A full sentence. */
  strand: string;
};

/**
 * ⚠️ **The three undo buttons on this card are irreversible once a line stops being UNFULFILLED, and
 * two of them move a ledger on the way out. This is the sentence that says so.**
 *
 * Every undo in `order-store.ts` is a TWO-ATTEMPT conditional write. Attempt 1 requires
 * `fulfillmentState = UNFULFILLED` and re-queues the line onto its worklist — the ordinary correction,
 * fully reversible. **Attempt 2 is a fallback with NO fulfilment check**: it flips the state and
 * `REMOVE`s `GSI1PK`/`GSI1SK` plus the audit stamps. So on a shipped line the undo *succeeds*, reverses
 * whatever was drawn, and drops the line off every worklist — and `queuePartitionFor` returns
 * `undefined` for anything not UNFULFILLED, so no reconcile or re-ingest ever puts it back.
 *
 * Reading attempt 1 alone says "the undo is guarded and harmless here". It is not, and that misreading
 * cost a wrong recommendation before the fallback was found. Do not re-derive this from one branch.
 *
 * Live at the time of writing: 20 packed ship-only lines, ~8 produced caps and 22 off-system lines are
 * all FULFILLED, i.e. ~50 rows each showing a button whose real effect is "spend, then strand".
 *
 * Returns `undefined` for an UNFULFILLED line — the ordinary path keeps its plain, quiet undo. Putting
 * friction there too is how operators learn to tap through the warning that matters (the same argument
 * `count-shelf-lib`'s implausible-entry guard makes for staying silent during routine counting).
 *
 * ⚠️ The `kind` precedence MIRRORS the row's own branch order (off-system → produce → pack). It is
 * derived here rather than passed in so the warning and the button cannot disagree about which control
 * they describe.
 */
export function shippedUndo(
  g: Pick<
    JobCardGarment,
    | 'fulfillmentState'
    | 'workflowState'
    | 'productionClass'
    | 'nonDtfWorkflowState'
    | 'fulfillWorkflowState'
    | 'packedItemSlug'
    | 'producedBlankSku'
    // ⚠️ WIDENED for the ship-from-stock review (F2): without these two the ALREADY_PRINTED arm
    // structurally could not tell a SHELF PICK from an off-system mark, and printed "no stock
    // moves" beside a control that reverses a real ledger draw.
    | 'stockPickId'
    | 'stockPickQty'
    // ⚠️ WIDENED for PR-6. These two are read by nothing in this function except the off-system arm's
    // `strand`, which asks {@link canMarkPrintedOffSystem} whether the way back will really be there.
    // They were absent, which is precisely how that sentence came to promise a door on a line the gate
    // refuses: the claim could not be tested because the fields were not in scope.
    | 'mappingStatus'
    | 'batchId'
  >,
): ShippedUndo | undefined {
  // The server's own predicate, not `=== 'FULFILLED'`: attempt 1's condition is `= UNFULFILLED`, so a
  // CANCELLED line takes the same irreversible fallback. 0 such rows today; the copy still must not be
  // able to lie about one later.
  if (g.fulfillmentState === 'UNFULFILLED') return undefined;
  const lead =
    g.fulfillmentState === 'CANCELLED'
      ? 'This order was cancelled.'
      : 'This order has already shipped.';

  // Ship-from-stock (PR-4, review F2): a picked line's ALREADY_PRINTED came from a real garment-
  // shelf draw, so it must NEVER take the off-system arm below — that arm's cost sentence ("no
  // stock moves") would be a false ledger claim, and its confirm fires the generic undo the server
  // gates 422 on. This arm's confirm fires the CREDITING route instead; the server still refuses a
  // non-UNFULFILLED line there (the pick stands), and the refusal copy names the intake door.
  if (g.workflowState === 'ALREADY_PRINTED' && g.stockPickId) {
    const cancelled = g.fulfillmentState === 'CANCELLED';
    return {
      kind: 'stock-pick',
      lead,
      cost: `${g.stockPickQty ?? 'Some'} shirt${g.stockPickQty === 1 ? '' : 's'} came off the garment shelf for this line — a real ledger draw.`,
      movesStock: true,
      strand: cancelled
        ? 'The server will refuse this undo — a cancelled line stays out of production. The shirts never shipped: add them back to the shelf (scan a hem tag, or Add shirts on the Printed garments tab).'
        : 'The server will refuse this undo — the shirts shipped, so the pick stands. Nothing will change.',
    };
  }
  if (g.workflowState === 'ALREADY_PRINTED') {
    return {
      kind: 'off-system',
      lead,
      // Marking printed off-system draws NOTHING — a blank is drawn at PRESS, by settleLine. So this
      // is the one of the three that costs no stock, and saying so keeps the other two credible.
      cost: 'Nothing was drawn when it was marked, so no stock moves.',
      movesStock: false,
      // ⚠️ REWRITTEN for PR-6, because the sentence it replaces became FALSE on all 22 live rows the
      // moment the forward mark landed on this card: "nothing in the app can reach it again".
      //
      // ⚠️ **DERIVED BY ASKING THE GATE, never asserted — and an adversarial review is why.** My first
      // rewrite branched on `fulfillmentState === 'CANCELLED'` alone, i.e. on ONE of the gate's five
      // clauses, and therefore promised a door to any shipped off-system line. It is wrong for at least
      // three reachable shapes: a line whose `mappingStatus` re-derived away from MAPPED (a refund arrives
      // as `orders/updated`, `computeMappingStatus` re-derives PAYMENT_PENDING for any financialStatus
      // outside {paid, partially_paid}, and `workflowState` is PRESERVED — so all 22 live rows are one
      // refund away from it); a line carrying a `batchId`; and a line reclassified to NON_DTF /
      // FULFILLMENT_ONLY, which this arm still describes because it is tested before both class arms.
      // On any of those the operator confirms a destructive clear on a promise, and the record is gone
      // with no door and no flag — `isUnrecordedFulfilledLine` is MAPPED-only, so the `N not recorded`
      // badge never picks it up either. Simulating the post-undo shape (`workflowState: 'OPEN'`; the undo
      // touches nothing else the gate reads) is the only way the two can't disagree — the same
      // "derived, never asserted" rule `shipOnlyPack.cost` and the produce arm's `drew` already follow.
      //
      // ⚠️ The pessimistic arm deliberately claims LESS than the sentence it replaced. It says this card
      // will not offer the mark again — true for every refusing shape — and stops there, because a
      // reclassified cap DOES get a different forward control ("Mark produced") one render later, so the
      // old universal "nothing in the app can reach it again" would have been its own new lie.
      strand: canMarkPrintedOffSystem({ ...g, workflowState: 'OPEN' })
        ? 'It clears who recorded it and when, and sends the line back to the print queue — which only lists unshipped orders, so this card is the only place that can mark it printed again.'
        : 'It clears who recorded it and when for good: the line goes back to the print queue, which only lists unshipped orders, and this card won’t offer to mark it printed again either.',
    };
  }
  if (g.productionClass === 'NON_DTF' && g.nonDtfWorkflowState === 'PRODUCED') {
    // ⚠️ Derived from the FROZEN draw identity, exactly like the pack branch below — a money review
    // caught this branch hardcoding `movesStock: true`. The server reverses only `if (drawnSku)`
    // against `producedBlankSku`, and absent is a DESIGNED state that `markLineProduced` writes
    // deliberately: an unmapped cap, a consume quantity of 0, or any cap produced before
    // consume-at-produce shipped (2026-07-30) — which is most of the live ones. Claiming a blank
    // returns where none was drawn is the same false statement the mockup made on the pack side.
    const drew = Boolean(g.producedBlankSku);
    return {
      kind: 'produce',
      lead,
      cost: drew
        ? 'Puts the cap blank it drew back on the shelf.'
        : 'No blank was recorded as drawn, so no stock moves.',
      movesStock: drew,
      // ⚠️ NOT "nothing in the app can mark it produced again" — that was false, and false on the very
      // row it printed on. `unmarkLineProduced` never touches `workflowState` (a cap's stays OPEN), so
      // one render later the SAME row matches the forward-produce branch and offers "Mark produced".
      // A shipped cap is round-trippable; only a CANCELLED one is not, because that branch excludes
      // CANCELLED. Saying so is also the more useful sentence: it names the way back.
      strand:
        g.fulfillmentState === 'CANCELLED'
          ? 'The line goes back to “to produce”, and nothing in the app can mark it produced again — the Caps worklist only lists unshipped orders, and a cancelled cap is offered no produce button here.'
          : 'The line goes back to “to produce”, and this card is the only place that can mark it produced again — the Caps worklist only lists unshipped orders. Its who/when stamps are cleared either way.',
    };
  }
  if (g.productionClass === 'FULFILLMENT_ONLY' && g.fulfillWorkflowState === 'PACKED') {
    // `packedItemSlug` is the item ACTUALLY drawn, frozen at pack — the server restores against that
    // and nothing else. Absent ⇒ the line was packed before its product was mapped, nothing was drawn,
    // and nothing goes back. Measured live: the packed decal on teamobs #O-4778 has no slug, so the
    // mockup's flat "Puts 1 back on the shelf" would have been a false statement on that very row.
    const drew = Boolean(g.packedItemSlug);
    return {
      kind: 'pack',
      lead,
      cost: drew
        ? 'Puts the shelf item it drew back in stock.'
        : 'Nothing was drawn at pack, so no stock moves.',
      movesStock: drew,
      strand:
        'The line goes back to “to pack”, but the Ship-only worklist only lists unshipped orders — so nothing in the app can mark it packed again.',
    };
  }
  return undefined;
}

/** What to stamp on the thumbnail: a short size token, and whether it is a youth size. */
export type GarmentSizeChip = { text: string; youth: boolean };

/**
 * The size stamped on a garment's photo — **the mitigation, not a decoration.**
 *
 * Shopify's `featuredImage` is selected on the PRODUCT, so a Black 3XL and a Red S of one shirt show
 * the identical picture. Beside a checkbox that consumes a blank, a picture that looks right for the
 * wrong variant is a mislabel affordance. Stamping the variant's own size on the image is what makes
 * a wrong picture contradict itself. If this ever returns nothing where it should return something,
 * the image is the part to remove — not the chip.
 *
 * Three branches, in order:
 *  - A size the shop's canon can name → the canonical short token ("X-Large" → `XL`, "Youth Small" →
 *    `YS`). Abbreviated through {@link normalizeSizeName} because the chip is a 25px box.
 *  - A NON_DTF cap whose variant carries no size at all → `OS`. Shopify writes `Default Title` and a
 *    null size exactly when a product has ONE variant, so "one size" is what that shape means. Gated
 *    on caps because it is only true of headwear: an apparel line with one variant is still a size,
 *    and a decal has no size to claim.
 *  - Anything else → nothing. A token the canon cannot place ("OSFA", "L/XL", a colourway) is left
 *    unstamped rather than guessed at; an unlabelled photo is a smaller problem than a wrong label.
 */
export function garmentSizeChip(
  g: Pick<JobCardGarment, 'sizeName' | 'productionClass'>,
): GarmentSizeChip | undefined {
  const token = normalizeSizeName(g.sizeName ?? '');
  if (isRealSize(token)) return { text: token, youth: isYouthSize(token) };
  if (g.productionClass === 'NON_DTF' && !g.sizeName) return { text: 'OS', youth: false };
  return undefined;
}

/** One garment (order line) as it stands in production. */
export type JobCardGarment = {
  key: string;
  lineId: string;
  title: string;
  variantTitle?: string;
  sku: string | null;
  /** The product's Shopify photo, joined at read time by the order-detail route (PR-4). Absent for a
   *  product-less line, an unsynced product, a product with no image, and a failed catalog read —
   *  all four mean "show the placeholder" and none of them means anything about the garment. */
  imageUrl?: string;
  /** The variant's own size as the catalog sync derived it ("X-Large", "4X-Large", "Youth Small").
   *  Read it through {@link garmentSizeChip}, never raw — the chip is a 25px box. */
  sizeName?: string;
  /**
   * How many of this garment the customer bought, net of refunds — `currentQty`, NOT `unfulfilledQty`.
   *
   * ⚠️ This used to be `unfulfilledQty` and that was wrong on most of the book. `unfulfilledQty` also
   * nets out FULFILLED units, so it reads 0 the moment a line ships: measured against the live ledger,
   * **859 of 1,008 lines read `unfulfilledQty: 0`, and 856 of those owe a real garment**. The card
   * therefore printed "×0" beside five garments in six — including beside the cap "Mark produced"
   * button, whose own copy had to apologise for it ("even when Qty shows 0 because the order already
   * shipped"). Same decision the production board took in PR-8 (`docs/production-ui-audit-and-plan-v1.md`
   * § 5.1): a piece is `currentQty`.
   *
   * The `??` fallback is a known under-count on a row last written before `currentQty` shipped
   * (measured: 2 of 1,008, both already `unfulfilledQty: 0`). Safe here because this number is
   * DISPLAYED, never spent — do not copy the fallback into anything that authorises work.
   *
   * ⚠️ CANCELLED lines are forced to 0: **Shopify does not zero `currentQty` on a cancellation**
   * (measured: `low-trendz/7494217433143` holds two refunded, cancelled lines still reading 5 and 4).
   * Rendering those would put "5" beside a "Cancelled" badge and claim five garments nobody owes —
   * the same reason `deriveOrderProduction` excludes cancelled lines from `pieces`. Read it through
   * {@link garmentQtyLabel} so the row and the header's piece count cannot disagree.
   */
  quantity: number;
  mappingStatus: OrderLineView['mappingStatus'];
  fulfillmentState: OrderLineView['fulfillmentState'];
  workflowState: OrderLineView['workflowState'];
  /** Non-DTF lane: a NON_DTF cap is produced off the sheet (its own OPEN → PRODUCED lifecycle). */
  productionClass?: OrderLineView['productionClass'];
  nonDtfWorkflowState?: OrderLineView['nonDtfWorkflowState'];
  /** Ship-only lane: a FULFILLMENT_ONLY line is packed in /fulfill (OPEN → PACKED lifecycle). */
  fulfillWorkflowState?: OrderLineView['fulfillWorkflowState'];
  /** Production-records R0: the who/when stamps the API always carried and the card used to hide.
   *  All last-write-only (an undo erases them — the R1 event log is the durable fix). */
  producedAt?: string;
  producedBy?: string;
  /** The cap blank actually drawn at produce, frozen. Absent ⇒ nothing was drawn, so an undo puts
   *  nothing back — read it through {@link shippedUndo}, never assume a draw happened. */
  producedBlankSku?: string;
  packedAt?: string;
  packedBy?: string;
  packedItemSlug?: string;
  /** The shelf item this variant maps to RIGHT NOW (live, not the frozen `packedItemSlug`). Absent ⇒
   *  unmapped ⇒ a pack draws nothing. Read it through {@link shipOnlyPack}. */
  stockItemSlug?: string;
  alreadyPrintedAt?: string;
  alreadyPrintedBy?: string;
  alreadyPrintedReason?: string;
  /** Ship-from-stock (finished-garment-stock v1 PR-4): present iff this line's ALREADY_PRINTED came
   *  from a shelf pick. Routes the undo to the shelf-crediting door and renders the stamp. */
  stockPickId?: string;
  stockPickQty?: number;
  pulledFromStockAt?: string;
  pulledFromStockBy?: string;
  /** The gang sheet this line was claimed into (absent while OPEN / never batched). */
  batchId?: string;
  batchSeq?: number;
  batchStatus?: BatchStatus;
  /** When the sheet was closed out (batch mark-printed) — shown beside the batch link. */
  batchPrintedAt?: string;
  /** DTF placements for this line on the sheet (0 when not batched or the sheet failed to load). */
  dtfPlacements: number;
  /** Screen-print placements for this line (empty when none / not batched / sheet unloaded). */
  screenPrint: ScreenPrintPlacement[];
  screenPrintPressed: number;
  screenPrintTotal: number;
  /** Every decoration placement (DTF + screen-print) on this garment, each with press state — the
   *  per-garment press checklist. Empty when not batched / the batch isn't loaded. */
  placements: GarmentPlacement[];
  pressedCount: number;
  placementTotal: number;
  /** All of this garment's placements are pressed (and it has ≥1). Tracking only in B1 — it does NOT
   *  advance the workflow status (that still flips at whole-batch mark-printed). */
  fullyPressed: boolean;
  /** `batchId` is set on the line but no sheet record is in hand — either still loading OR the fetch
   *  failed. This pure fn can't tell which; the page distinguishes via the react-query state. */
  batchMissing: boolean;
};

/** A distinct gang sheet this order's garments belong to, with this order's screen-print progress. */
export type JobCardSheet = {
  batchId: string;
  seq?: number;
  status?: BatchStatus;
  /** Set when the sheet is itself a per-garment reprint of another sheet. */
  reissueOf?: string;
  /** Screen-print progress SCOPED TO THIS ORDER's placements on the sheet (not the whole sheet). */
  screenPrintPressed: number;
  screenPrintTotal: number;
  /** The sheet header loaded; false = known via a line's batchId but the fetch failed. */
  loaded: boolean;
};

export type JobCard = {
  order: OrderSummary;
  production: OrderProduction;
  garments: JobCardGarment[];
  /** Distinct sheets this order touches (in first-appearance order), for a "jump to sheet" summary. */
  sheets: JobCardSheet[];
  /** Any line carries the allow-listed rush tag (order-scoped). */
  rush: boolean;
  /** Earliest due date across the order's lines (ISO `YYYY-MM-DD`), or null. */
  dueDate: string | null;
  /**
   * THE ship answer for this order: every producible line is PRINTED **and** no reprint is outstanding
   * **and** the reprint lookup actually answered. False whenever any of those is unknown — see
   * `reprintChecked`. This is the only field a "safe to hand to shipping" affordance may key on;
   * `production.status === 'PRINTED'` is NOT sufficient (decision D4 / §8.25) because the line ledger
   * cannot see a reprint — a reissue batch never repoints the source line's `batchId`.
   */
  productionComplete: boolean;
  /** Outstanding reprints (unpressed reissue batches) touching this order. 0 when none AND when unknown —
   *  always read it with `reprintChecked`. */
  reprintOutstanding: number;
  /** The `GET /orders/:store/:id/reprints` lookup has answered. FALSE while it loads or after it fails,
   *  and then this order must not present as shippable: the order detail resolves BEFORE the reprint
   *  lookup, so keying a green "done" state on the order alone flashes a ship-go for an order that is
   *  about to be revealed as not shippable. Unknown is not safe. */
  reprintChecked: boolean;
  /** The whole order is pressed and nothing upstream is still owed: ≥1 pressed garment, every
   *  pressable garment fully pressed, no needs-action line, no still-OPEN producible line, and every
   *  referenced sheet loaded. In B1 this can be true BEFORE the workflow status is PRINTED (pressing is
   *  tracked but doesn't yet flip completion) — the page uses it to nudge the operator to mark the
   *  batch printed. Guarded (see buildJobCard) so the nudge can't over-claim on a partial order. */
  allGarmentsPressed: boolean;
  /** UNMAPPED / BLOCKED / PAYMENT_PENDING lines — can't be produced until resolved. */
  needsActionCount: number;
};

export type GarmentTone = 'attention' | 'progress' | 'done' | 'muted';
export type GarmentStage = { label: string; tone: GarmentTone };

/**
 * The per-garment stage label. Fulfillment outranks workflow (a cancelled/fulfilled line owes no
 * production regardless of its workflow state); mapping gates everything before it. Mirrors the
 * precedence in the server's `deriveOrderProduction`.
 */
export function garmentStage(
  g: Pick<
    JobCardGarment,
    | 'mappingStatus'
    | 'fulfillmentState'
    | 'workflowState'
    | 'productionClass'
    | 'nonDtfWorkflowState'
    | 'fulfillWorkflowState'
    | 'stockPickId'
  >,
): GarmentStage {
  if (g.fulfillmentState === 'CANCELLED') return { label: 'Cancelled', tone: 'muted' };
  if (g.fulfillmentState === 'FULFILLED') return { label: 'Fulfilled', tone: 'done' };
  // Non-DTF cap (produced off the gang sheet in the caps worklist) — its own OPEN → PRODUCED
  // lifecycle, not the DTF mapping/workflow. Show it as a cap, not the generic "Not apparel".
  if (g.productionClass === 'NON_DTF') {
    return g.nonDtfWorkflowState === 'PRODUCED'
      ? { label: 'Cap — produced', tone: 'done' }
      : { label: 'Cap — to produce', tone: 'muted' };
  }
  // Ship-only stocked item (inventory-clarity P2) — packed in the /fulfill worklist, never produced.
  // Replaces the dead-end "Not apparel" for goods FSP actually stocks and ships.
  if (g.productionClass === 'FULFILLMENT_ONLY') {
    return g.fulfillWorkflowState === 'PACKED'
      ? { label: 'Ship-only — packed', tone: 'done' }
      : { label: 'Ship-only — to pack', tone: 'muted' };
  }
  // Marked already-printed off-system → done, regardless of mapping (mirrors deriveOrderProduction:
  // a CUSTOMER_SUPPLIED line is UNMAPPED but still done).
  if (g.workflowState === 'ALREADY_PRINTED') {
    // A shelf pick and an off-system mark share the state; the badge must name the right mechanism
    // (review F9) — "printed off-system" on a picked line misattributes a real ledger draw.
    return g.stockPickId
      ? { label: 'Shipped from shelf stock', tone: 'done' }
      : { label: 'Printed off-system', tone: 'done' };
  }
  // Transfer pulled off the printed-transfer shelf instead of reprinting (Phase 2) → done off the sheet.
  if (g.workflowState === 'PULLED_FROM_STOCK') return { label: 'Pulled from stock', tone: 'done' };
  switch (g.mappingStatus) {
    case 'UNMAPPED':
      return { label: 'Unmapped', tone: 'attention' };
    case 'BLOCKED':
      return { label: 'Blocked', tone: 'attention' };
    case 'PAYMENT_PENDING':
      return { label: 'Payment pending', tone: 'attention' };
    case 'EXCLUDED':
      return { label: 'Not apparel', tone: 'muted' };
    default:
      break; // MAPPED — fall through to the workflow state
  }
  switch (g.workflowState) {
    case 'OPEN':
      return { label: 'Queued — not on a sheet yet', tone: 'muted' };
    case 'BATCHED':
      return { label: 'On sheet — printing', tone: 'progress' };
    case 'PRINTED':
      return { label: 'Printed', tone: 'done' };
    case 'SHIPPED':
      return { label: 'Shipped', tone: 'done' };
    default:
      return { label: g.workflowState, tone: 'muted' };
  }
}

/**
 * Which lane a garment belongs to. The order card used to render `detail.lines` 1:1 in raw server
 * order, so on a four-class order a cap needing production and a decal needing nothing sat as
 * adjacent muted rows distinguished only by badge text, and nothing on the page said the job was made
 * of four different kinds of work.
 *
 * ⚠️ GROUPED, deliberately not SORTED by "needs you first". This page fires `settleLine` from a
 * checkbox, and rows that reorder as work completes are the documented way a mis-tap spends a blank.
 * A garment's LANE never changes — only its state does — so grouping is stable under every action the
 * page offers, which a priority sort is not.
 *
 * ⚠️ CLASS BEATS MAPPING, matching {@link garmentStage}'s own precedence. Every cap and ship-only line
 * carries `mappingStatus: 'EXCLUDED'`, so a mapping-first rule would empty the two lanes entirely.
 * The consequence is that a BLOCKED cap lands in the caps lane rather than needs-action — the same
 * answer its stage badge already gives, which is the point: lane and badge must not contradict.
 */
export type GarmentLaneKey =
  | 'needsAction'
  | 'apparel'
  | 'caps'
  | 'shipOnly'
  | 'notProduced'
  | 'cancelled';

export function garmentLaneKey(
  g: Pick<JobCardGarment, 'mappingStatus' | 'fulfillmentState' | 'productionClass'>,
): GarmentLaneKey {
  if (g.fulfillmentState === 'CANCELLED') return 'cancelled';
  if (g.productionClass === 'NON_DTF') return 'caps';
  if (g.productionClass === 'FULFILLMENT_ONLY') return 'shipOnly';
  if (
    g.mappingStatus === 'UNMAPPED' ||
    g.mappingStatus === 'BLOCKED' ||
    g.mappingStatus === 'PAYMENT_PENDING'
  ) {
    return 'needsAction';
  }
  if (g.mappingStatus === 'EXCLUDED') return 'notProduced';
  return 'apparel';
}

export type GarmentLane = {
  key: GarmentLaneKey;
  title: string;
  blurb: string;
  /** The unit this lane counts in — "garment", "cap", "item". */
  noun: string;
  attention: boolean;
  garments: JobCardGarment[];
};

/** Lane chrome + the fixed display order. Worst-first, then by how much work the lane represents. */
const LANE_META: Record<
  GarmentLaneKey,
  { title: string; blurb: string; noun: string; attention: boolean }
> = {
  needsAction: {
    title: 'Needs action before anything can be made',
    blurb: 'unmapped, blocked or unpaid — nothing can be built until these are resolved',
    noun: 'garment',
    attention: true,
  },
  apparel: {
    title: 'Apparel — gang sheet',
    blurb: 'printed on film, pressed here',
    noun: 'garment',
    attention: false,
  },
  caps: {
    title: 'Caps — produced off-sheet',
    blurb: 'produced off the gang sheet, never on film',
    noun: 'cap',
    attention: false,
  },
  shipOnly: {
    title: 'Ship-only — from the shelf',
    blurb: 'nothing is made; pick it and pack it',
    noun: 'item',
    attention: false,
  },
  notProduced: {
    title: 'Not made here',
    blurb: 'bought in and shipped as-is',
    noun: 'item',
    attention: false,
  },
  cancelled: {
    title: 'Cancelled',
    blurb: 'nothing owed',
    noun: 'line',
    attention: false,
  },
};

const LANE_ORDER: GarmentLaneKey[] = [
  'needsAction',
  'apparel',
  'caps',
  'shipOnly',
  'notProduced',
  'cancelled',
];

/** Non-empty lanes in the fixed order, each keeping the server's line order within it. */
export function groupGarmentsByLane(garments: readonly JobCardGarment[]): GarmentLane[] {
  const byLane = new Map<GarmentLaneKey, JobCardGarment[]>();
  for (const g of garments) {
    const key = garmentLaneKey(g);
    const arr = byLane.get(key);
    if (arr) arr.push(g);
    else byLane.set(key, [g]);
  }
  return LANE_ORDER.filter((k) => byLane.has(k)).map((key) => ({
    key,
    ...LANE_META[key],
    garments: byLane.get(key) ?? [],
  }));
}

/** "3 garments · 3 pieces" — rows and the garments they represent are different numbers on a qty-N line. */
export function laneCountLabel(lane: Pick<GarmentLane, 'noun' | 'garments'>): string {
  const rows = lane.garments.length;
  const pieces = lane.garments.reduce((n, g) => n + g.quantity, 0);
  const head = `${rows} ${lane.noun}${rows === 1 ? '' : 's'}`;
  return `${head} · ${pieces} piece${pieces === 1 ? '' : 's'}`;
}

/**
 * What to say in a row's action zone when there is no control to offer.
 *
 * ⚠️ The point is that it is NEVER empty. The card used to render a bare em-dash for every one of
 * these, and an unexplained "—" is indistinguishable from a bug — which on the unpacked ship-only
 * row it literally was. Where another surface can act, the note names it so the operator has
 * somewhere to go; where nothing in the app can act yet, it says so rather than implying the
 * operator missed a button.
 *
 * Returns null when the row HAS an action — the caller renders the control instead.
 */
export type GarmentIdleNote = {
  text: string;
  /** A surface that can act on this garment, when one exists. */
  to?: '/fulfill' | '/caps' | '/orders' | '/batches';
  linkLabel?: string;
};

export function garmentIdleNote(
  g: Pick<
    JobCardGarment,
    | 'mappingStatus'
    | 'fulfillmentState'
    | 'workflowState'
    | 'productionClass'
    | 'nonDtfWorkflowState'
    | 'fulfillWorkflowState'
    | 'batchId'
    | 'placementTotal'
  >,
  sheetFailed = false,
): GarmentIdleNote | null {
  if (g.fulfillmentState === 'CANCELLED') return { text: 'Cancelled — nothing owed.' };
  if (g.productionClass === 'FULFILLMENT_ONLY' && g.fulfillWorkflowState !== 'PACKED') {
    // ⚠️ REWRITTEN for PR-5b. Both of the old sentences described a card with no forward control:
    // "Shipped before the pack was recorded — nothing in the app can record it yet" (which this PR
    // makes FALSE — the 8 stranded lines are exactly what its button is for), and a pointer at the
    // Ship-only worklist.
    //
    // Reaching this note now means `shipOnlyPack` REFUSED, and exactly ONE of its four conditions can
    // land here: the line's DTF `workflowState` is not OPEN — a line reclassified to ship-only while a
    // gang sheet still owned it. (Its CANCELLED arm cannot reach this point: the cancelled note above
    // returns first. I wrote a branch for it here and the test proved it dead — no unreachable copy.)
    //
    // `markLinePacked` requires OPEN, and so does `queuePartitionFor`'s FULFILLONLY partition, so the
    // worklist has no row for it either and there is nothing to point at. Deliberately NOT a link,
    // for the same reason the caps arm below is not one.
    return { text: 'Reclassified while on a sheet — packing isn’t available from here.' };
  }
  if (g.productionClass === 'NON_DTF' && g.nonDtfWorkflowState !== 'PRODUCED') {
    // Reaching here means the forward produce was refused by its own gate (workflowState is not
    // OPEN — a cap reclassified while it was on a sheet). The server refuses it too, so pointing at
    // the caps worklist would be sending the operator to a row that is not there.
    return { text: 'Reclassified while on a sheet — produce isn’t available from here.' };
  }
  if (g.workflowState === 'PULLED_FROM_STOCK') {
    return { text: 'Pulled from the transfer shelf — nothing to press.' };
  }
  if (g.mappingStatus === 'EXCLUDED') return { text: 'Not made here — ships as bought.' };
  if (
    g.mappingStatus === 'UNMAPPED' ||
    g.mappingStatus === 'BLOCKED' ||
    g.mappingStatus === 'PAYMENT_PENDING'
  ) {
    return {
      text: 'Resolve this before it can be built.',
      to: '/orders',
      linkLabel: 'Orders queue',
    };
  }
  if (sheetFailed) {
    return { text: 'Sheet couldn’t load — press check-off unavailable. Try Refresh.' };
  }
  if (!g.batchId) {
    // A MAPPED apparel line with no sheet. Two very different reasons.
    //
    // ⚠️ Since PR-6 the FULFILLED arm is PROVEN-UNREACHABLE DEFENSIVE COPY, and it is kept deliberately.
    // A shipped-and-unrecorded line whose `workflowState` is OPEN now gets a real "Mark printed
    // off-system" button (`canMarkPrintedOffSystem`) and `GarmentRow` renders the control INSTEAD of this
    // note. Nothing else can get here: an adversarial review enumerated every `workflowState` writer in
    // `rest-api/src` and **no writer produces `SHIPPED`**; `BATCHED` sets `batchId` in the same
    // expression; `PRINTED` is conditioned on `batchId`; `PULLED_FROM_STOCK` and every non-MAPPED shape
    // are caught by earlier arms above. (An earlier version of this comment said "the odd shape —
    // sheetless with a non-OPEN workflow", which names a state no writer can produce. Corrected rather
    // than deleted, because the wrong sentence was mine.)
    //
    // KEPT rather than removed, unlike the dead CANCELLED branch PR-5b deleted from this same function:
    // dropping the ternary would send a FULFILLED line down the UNFULFILLED arm and print "Waiting for a
    // gang sheet." with a link to a page that structurally cannot list it — a worse failure than one
    // unreachable true sentence. The rendered-row test in `order-detail-off-system.test.tsx` is what
    // proves the note is no longer what an operator sees.
    //
    // The UNFULFILLED arm is unchanged and is now load-bearing: it is the ONLY thing pointing that
    // population at `/orders`, which is deliberately where its (queue-dropping, demand-dropping) mark
    // stays. See `canMarkPrintedOffSystem` for why that half is not offered here.
    return g.fulfillmentState === 'FULFILLED'
      ? { text: 'Shipped without its production being recorded.' }
      : { text: 'Waiting for a gang sheet.', to: '/orders', linkLabel: 'Orders queue' };
  }
  if (g.placementTotal === 0) return { text: 'On a sheet, but it carries no placements.' };
  return null;
}

/**
 * Whether this garment still owes production, has had it, or was never going to.
 *
 * ⚠️ CLASS IS CHECKED BEFORE WORKFLOW, and that ordering is the whole point — it is what closes the
 * one false ship-go this surface had. Verified by executing the server's own rollup: a lane line whose
 * product is reclassified WHILE IT IS ON A SHEET keeps `workflowState: 'BATCHED'`, misses both lane
 * branches in `deriveOrderProduction` (they require OPEN), falls to its EXCLUDED arm and counts as
 * `nonDtf` — contributing nothing to `batched` or `openWork`. A printed DTF sibling then carries the
 * whole order to `status: 'PRINTED'`, and the card rendered "Every garment on this order is printed
 * and pressed — ready to hand to shipping" directly above that same row's live, unticked press
 * checkbox. Here the same line reads NON_DTF-and-not-produced ⇒ `owed`, and the order is refused.
 *
 * ⚠️ FULFILLED is `none`, not `done`. The question this answers is "can the order go to shipping",
 * and a line Shopify has already shipped cannot block that — whatever we did or did not record for
 * it. (Whether its production was ever recorded is a real and separate problem, and the row says so
 * in its own action cell; it is not a reason to hold the rest of the order.)
 *
 * ⚠️ The fail direction is deliberate: anything unrecognised falls through to `owed`, so an unmodelled
 * state refuses a ship-go rather than granting one. Refusing costs a phone call; granting ships short.
 */
export type GarmentOutcome = 'owed' | 'done' | 'none';

export function garmentOutcome(
  g: Pick<
    JobCardGarment,
    | 'mappingStatus'
    | 'fulfillmentState'
    | 'workflowState'
    | 'productionClass'
    | 'nonDtfWorkflowState'
    | 'fulfillWorkflowState'
  >,
): GarmentOutcome {
  if (g.fulfillmentState === 'CANCELLED') return 'none';
  if (g.fulfillmentState === 'FULFILLED') return 'none';
  /**
   * ⚠️ ON A SHEET ⇒ OWED, ahead of every class and mapping rule. BATCHED means "claimed onto a gang
   * sheet", never "printed", so this can only ever be work outstanding.
   *
   * This closes a FALSE GO an adversarial review found in my first cut, which had fixed the class arm
   * and left the mapping arm open — the same hazard one door over. Executed then: a line
   * `{mappingStatus: 'EXCLUDED', workflowState: 'BATCHED'}` whose productionClass is NOT_PRODUCED,
   * UNCLASSIFIED, DTF_APPAREL or absent skipped both class branches, hit the EXCLUDED arm, and read
   * `none` — so an order holding a garment physically on the press with unpressed placements
   * announced "Everything on this order is made — ready to hand to shipping". The same edit also
   * closes the twin where a cap or ship-only line reads terminal on its LANE (PRODUCED / PACKED)
   * while its DTF workflow is still BATCHED.
   *
   * Placing it above the class arms rather than adding a `workflowState` term to each is deliberate:
   * one rule cannot be half-applied, and a class the enum grows later inherits it for free.
   */
  if (g.workflowState === 'BATCHED') return 'owed';
  if (g.productionClass === 'NON_DTF') {
    return g.nonDtfWorkflowState === 'PRODUCED' ? 'done' : 'owed';
  }
  if (g.productionClass === 'FULFILLMENT_ONLY') {
    return g.fulfillWorkflowState === 'PACKED' ? 'done' : 'owed';
  }
  if (g.workflowState === 'ALREADY_PRINTED' || g.workflowState === 'PULLED_FROM_STOCK') {
    return 'done';
  }
  // Bought in and shipped as-is — nothing was ever going to be made.
  if (g.mappingStatus === 'EXCLUDED') return 'none';
  if (
    g.mappingStatus === 'UNMAPPED' ||
    g.mappingStatus === 'BLOCKED' ||
    g.mappingStatus === 'PAYMENT_PENDING'
  ) {
    return 'owed';
  }
  if (g.workflowState === 'PRINTED' || g.workflowState === 'SHIPPED') return 'done';
  return 'owed'; // OPEN, BATCHED, and anything the enum grows later
}

/**
 * Whether this shop was ever going to make or pack this line — class and mapping only, deliberately
 * ignoring fulfilment and cancellation.
 *
 * ⚠️ Exists because {@link garmentOutcome} folds "already shipped" into `none`, which is right for
 * "can this block the order" and WRONG for "did this order have work in it". Without the split, a
 * fully-shipped order — every line `none` — read as "Nothing to make on this order. Every line is
 * bought in and ships as it arrived", on an order that had five garments made for it. Caught by
 * opening a real shipped order in a browser; no assertion I had written could see it.
 */
export function garmentIsProducible(
  g: Pick<JobCardGarment, 'mappingStatus' | 'fulfillmentState' | 'productionClass'>,
): boolean {
  if (g.fulfillmentState === 'CANCELLED') return false;
  if (g.productionClass === 'NON_DTF' || g.productionClass === 'FULFILLMENT_ONLY') return true;
  return g.mappingStatus !== 'EXCLUDED';
}

export type ShipVerdictState = 'go' | 'stop' | 'unknown' | 'nothing' | 'shipped';

export type ShipVerdict = {
  state: ShipVerdictState;
  headline: string;
  reason: string;
  /** Per-lane progress, for the chips beside the verdict. Producible lanes only. */
  lanes: { key: GarmentLaneKey; label: string; done: number; total: number }[];
};

const VERDICT_LANE_LABEL: Partial<Record<GarmentLaneKey, string>> = {
  needsAction: 'Needs action',
  apparel: 'Apparel',
  caps: 'Caps',
  shipOnly: 'Ship-only',
};

/** "3 to print · 1 cap to produce · 2 to pack" — what is actually outstanding, by lane. */
function outstandingReason(garments: readonly JobCardGarment[]): string {
  const owed = garments.filter((g) => garmentOutcome(g) === 'owed');
  const by = (k: GarmentLaneKey) => owed.filter((g) => garmentLaneKey(g) === k).length;
  const parts: string[] = [];
  const blocked = by('needsAction');
  const apparel = by('apparel');
  const caps = by('caps');
  const ship = by('shipOnly');
  if (blocked) parts.push(`${blocked} to resolve first`);
  if (apparel) parts.push(`${apparel} still to print`);
  if (caps) parts.push(`${caps} cap${caps === 1 ? '' : 's'} to produce`);
  if (ship) parts.push(`${ship} to pack`);
  // ⚠️ Never return an empty string. Every clause above is conditional, so a lane the list forgets —
  // or a future one — would produce "Not ready to ship" with a blank explanation, and an operator
  // cannot tell a real blocker from a rendering fault.
  return parts.length > 0 ? parts.join(' · ') : `${owed.length} still to finish`;
}

/**
 * THE ship answer for this order, in one function so the badge, the banner and the strip cannot drift
 * into disagreeing — which they did: the headline badge went green for a PRODUCED or PACKED order
 * while the green banner beside it stayed hidden, because the banner required `status === 'PRINTED'`.
 *
 * ⚠️ Derived from the GARMENTS, not from `production.status`. The card holds every line's class,
 * workflow state and lane state, which is strictly more than the rollup's single enum — and the rollup
 * has a hole this cannot have (see {@link garmentOutcome}). The cost is that the card can now refuse an
 * order the production BOARD still shows as ready, because the board reads the same holed rollup. That
 * divergence is in the safe direction and wants its own server-side fix.
 *
 * ⚠️ `unknown` outranks everything except an outstanding reprint. The order detail resolves BEFORE the
 * reprint lookup, so a verdict that ignored "not answered yet" would flash a ship-go and then retract
 * it. Unknown is not safe (D4 / §8.25).
 */
export function shipVerdict(
  card: Pick<JobCard, 'garments' | 'reprintChecked' | 'reprintOutstanding'>,
  reprintGarmentCount = card.reprintOutstanding,
): ShipVerdict {
  const producible = card.garments.filter((g) => garmentOutcome(g) !== 'none');
  const owed = producible.filter((g) => garmentOutcome(g) === 'owed');
  const lanes = (['needsAction', 'apparel', 'caps', 'shipOnly'] as const)
    .map((key) => {
      const inLane = producible.filter((g) => garmentLaneKey(g) === key);
      return {
        key: key as GarmentLaneKey,
        label: VERDICT_LANE_LABEL[key] ?? key,
        done: inLane.filter((g) => garmentOutcome(g) === 'done').length,
        total: inLane.length,
      };
    })
    .filter((l) => l.total > 0);

  if (card.reprintOutstanding > 0) {
    const n = reprintGarmentCount;
    // ⚠️ An order Shopify has already shipped cannot be "not shipped yet". Telling the office "Do not
    // ship" on the re-send path — which is live in prod — is a contradiction with the green "Shipped"
    // badge beside it, and `SHIP_CLAIM_STEM` deliberately leaves SHIPPED ungated because there is no
    // ship decision left to protect there.
    if (producible.length === 0 && card.garments.some((g) => g.fulfillmentState === 'FULFILLED')) {
      return {
        state: 'stop',
        headline: `Reprint outstanding — ${n} garment${n === 1 ? '' : 's'}`,
        reason: 'This order already shipped, so this is a re-send.',
        lanes,
      };
    }
    return {
      state: 'stop',
      // Kept SHORT on purpose: this was the tallest state, and on a phone its headline wrapped to a
      // second line, making the strip 36px taller than the others — enough to move a press checkbox
      // under a finger when a reprint appears on a refetch. The rows carry the detail and the link.
      headline: `Do not ship — ${n} still to reprint`,
      reason: 'Marked on the rows below.',
      lanes,
    };
  }
  /**
   * ⚠️ OWED IS CHECKED BEFORE UNKNOWN. Both withhold the go, so D4 is satisfied either way — but an
   * order with five outstanding garments needs no reprint information to be refused, and putting
   * `unknown` first demoted that known refusal to the blue "can't confirm" tone. Measured: an order
   * with owed garments and a failed reprint lookup rendered "Can't confirm this order is ready" —
   * softer than the truth, on the one element now sitting above the press checkboxes. It also made
   * `orderHeadline`'s "production not finished" arm structurally unreachable in that window, so the
   * badge named the reprint check when the real blocker was a cap still to produce.
   */
  if (owed.length > 0) {
    return {
      state: 'stop',
      headline: 'Not ready to ship',
      reason: outstandingReason(producible),
      lanes,
    };
  }
  if (!card.reprintChecked) {
    return {
      state: 'unknown',
      headline: 'Can’t confirm this order is ready',
      reason: 'The reprint check didn’t answer — try Refresh.',
      lanes,
    };
  }
  if (producible.length === 0) {
    // Two very different reasons to have nothing outstanding, and saying the wrong one is a false
    // statement about a real order — see `garmentIsProducible`.
    const everMade = card.garments.filter(garmentIsProducible);
    if (everMade.length === 0) {
      // ⚠️ THREE reasons to have nothing outstanding, not two. A wholly cancelled order also reaches
      // here with `everMade` empty — `garmentIsProducible` refuses a cancelled line — and telling the
      // operator "every line is bought in and ships as it arrived" about an order that was cancelled
      // is the same class of false statement the shipped-vs-nothing split already fixed once.
      if (
        card.garments.length > 0 &&
        card.garments.every((g) => g.fulfillmentState === 'CANCELLED')
      ) {
        return {
          state: 'nothing',
          headline: 'This order was cancelled',
          reason: 'Nothing is owed on it.',
          lanes,
        };
      }
      return {
        state: 'nothing',
        headline: 'Nothing to make on this order',
        reason: 'Every line is bought in and ships as it arrived.',
        lanes,
      };
    }
    return {
      state: 'shipped',
      headline: 'This order has shipped',
      reason: 'Nothing is outstanding.',
      lanes,
    };
  }
  return {
    state: 'go',
    headline: 'Everything on this order is made',
    reason: 'No reprint outstanding — ready to hand to shipping.',
    lanes,
  };
}

export const GARMENT_TONE_CLASS: Record<GarmentTone, string> = {
  attention:
    'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300',
  progress:
    'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950 dark:text-blue-300',
  done: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-300',
  muted: 'border-border bg-muted text-muted-foreground',
};

export const PRODUCTION_STATUS_LABEL: Record<OrderProductionStatus, string> = {
  NOT_STARTED: 'Not started',
  IN_PRODUCTION: 'In production',
  PRINTED: 'Printed · ready for shipping',
  PRODUCED: 'Produced · ready for shipping',
  PACKED: 'Packed · ready for shipping',
  SHIPPED: 'Shipped',
  NEEDS_ACTION: 'Needs action',
  CANCELLED: 'Cancelled',
  NO_DTF: 'No DTF production',
};

/** Tone for the raw rollup status, ignoring reprints. Private — everything goes through `orderHeadline`. */
function statusTone(status: OrderProductionStatus): GarmentTone {
  switch (status) {
    case 'NEEDS_ACTION':
      return 'attention';
    case 'IN_PRODUCTION':
      return 'progress';
    case 'PRINTED':
    case 'PRODUCED':
    case 'PACKED':
    case 'SHIPPED':
      return 'done';
    default:
      return 'muted';
  }
}

/**
 * The statuses whose label CLAIMS the order is ready to hand to shipping, mapped to the stem the
 * reprint-aware headline reuses. Only these need the D4 gate — every other status already reads as
 * unfinished, cancelled or out of scope.
 *
 * SHIPPED is deliberately ABSENT: Shopify has already shipped it, so there is no ship decision left to
 * protect and re-tinting it would misreport a closed order. A late reprint there is a re-send, and the
 * reprint alert still shows.
 */
const SHIP_CLAIM_STEM: Partial<Record<OrderProductionStatus, string>> = {
  PRINTED: 'Printed',
  PRODUCED: 'Produced',
  // Ship-only (P2): "Packed · ready for shipping" is a ship claim like the other two — a DTF sibling's
  // outstanding reprint must veto it the same way (§8.25: gate EVERY surface that claims ship-ready).
  PACKED: 'Packed',
};

export type OrderHeadline = { label: string; tone: GarmentTone };

/**
 * The order's headline badge — label and tone from ONE function so they cannot drift into a green tone
 * next to a "not ready" label. Reprint-aware (decision D4 / §8.25): an order whose reprint is still on the
 * press, or whose reprint lookup hasn't answered, must not wear a done-tone "ready for shipping" badge.
 */
export function orderHeadline(
  card: Pick<JobCard, 'production' | 'reprintOutstanding' | 'reprintChecked' | 'garments'>,
): OrderHeadline {
  const status = card.production.status;
  const stem = SHIP_CLAIM_STEM[status];
  if (stem !== undefined) {
    if (card.reprintOutstanding > 0) {
      return { label: `${stem} · reprint on the press`, tone: 'attention' };
    }
    /**
     * ⚠️ The badge is a ship-claiming surface too, so it gets the same gate as the banner (§8.25:
     * gate EVERY surface that claims ship-ready). Without this the two could disagree in the
     * dangerous direction — the rollup laundering a reclassified-onto-a-sheet lane line into
     * `PRINTED` while the garment-derived verdict refuses. Checked AFTER the reprint arms so their
     * more specific wording still wins.
     */
    if (
      shipVerdict({
        garments: card.garments,
        reprintChecked: card.reprintChecked,
        reprintOutstanding: card.reprintOutstanding,
      }).state === 'stop'
    ) {
      return { label: `${stem} · production not finished`, tone: 'attention' };
    }
    if (!card.reprintChecked) {
      // "unknown", not "checking": `reprintChecked` is false while the lookup is in flight AND after it
      // fails, and a label that only described the in-flight case would be a small lie in the case that
      // actually matters. Blue rather than amber — the page's error alert carries the alarm; this badge's
      // only job is to not be GREEN.
      return { label: `${stem} · reprint status unknown`, tone: 'progress' };
    }
  }
  return { label: PRODUCTION_STATUS_LABEL[status], tone: statusTone(status) };
}

/** Aggregate order-scoped priority from the lines (any rush → rush; earliest non-null due date). */
function aggregatePriority(lines: readonly OrderLineView[]): {
  rush: boolean;
  dueDate: string | null;
} {
  let rush = false;
  let dueDate: string | null = null;
  for (const l of lines) {
    if (l.rush) rush = true;
    const d = l.dueDate ?? null;
    if (d && (dueDate === null || d < dueDate)) dueDate = d;
  }
  return { rush, dueDate };
}

function toGarment(line: OrderLineView, batchesById: Map<string, BatchDetail>): JobCardGarment {
  const batchId = line.batchId;
  const batch = batchId ? batchesById.get(batchId) : undefined;
  // A sheet spans multiple orders — match the server's FULL placement key (storeId+orderId+lineId),
  // not lineId alone, so a foreign order that reuses this lineId on the same sheet can't leak in.
  const isThisLine = (r: { storeId: string; orderId: string; lineId: string }) =>
    r.storeId === line.storeId && r.orderId === line.orderId && r.lineId === line.lineId;
  const screenPrint: ScreenPrintPlacement[] = batch
    ? (batch.batch.screenPrintWork ?? []).filter(isThisLine).map((w) => ({
        location: w.location,
        designName: w.designName,
        quantity: w.quantity,
        pressed: Boolean(w.pressedAt),
        pressedAt: w.pressedAt,
      }))
    : [];
  const dtfItems = batch ? batch.items.filter(isThisLine) : [];
  // The unified per-garment press checklist: DTF placements first (pressed on the heat press off the
  // sheet), then screen-print placements. Both carry a `pressed` flag from their own `pressedAt`.
  const placements: GarmentPlacement[] = [
    ...dtfItems.map((i) => ({
      kind: 'dtf' as const,
      location: i.location,
      designName: i.designName,
      pressed: Boolean(i.pressedAt),
      pressedAt: i.pressedAt,
      fromStock: i.pulledFromStock,
    })),
    ...screenPrint.map((p) => ({
      kind: 'screen-print' as const,
      location: p.location,
      designName: p.designName,
      pressed: p.pressed,
      pressedAt: p.pressedAt,
    })),
  ];
  const pressedCount = placements.filter((p) => p.pressed).length;
  return {
    key: line.lineId,
    lineId: line.lineId,
    title: line.title,
    variantTitle: line.variantTitle,
    sku: line.sku,
    imageUrl: line.imageUrl,
    sizeName: line.sizeName,
    // See the field's doc on JobCardGarment for why this is `currentQty`, why cancelled is forced to
    // 0, and what the `??` fallback costs.
    quantity: line.fulfillmentState === 'CANCELLED' ? 0 : (line.currentQty ?? line.unfulfilledQty),
    mappingStatus: line.mappingStatus,
    fulfillmentState: line.fulfillmentState,
    workflowState: line.workflowState,
    productionClass: line.productionClass,
    nonDtfWorkflowState: line.nonDtfWorkflowState,
    fulfillWorkflowState: line.fulfillWorkflowState,
    producedAt: line.producedAt,
    producedBy: line.producedBy,
    producedBlankSku: line.producedBlankSku,
    packedAt: line.packedAt,
    packedBy: line.packedBy,
    packedItemSlug: line.packedItemSlug,
    stockItemSlug: line.stockItemSlug,
    alreadyPrintedAt: line.alreadyPrintedAt,
    alreadyPrintedBy: line.alreadyPrintedBy,
    alreadyPrintedReason: line.alreadyPrintedReason,
    stockPickId: line.stockPickId,
    stockPickQty: line.stockPickQty,
    pulledFromStockAt: line.pulledFromStockAt,
    pulledFromStockBy: line.pulledFromStockBy,
    batchId,
    batchSeq: batch?.batch.seq,
    batchStatus: batch?.batch.status,
    batchPrintedAt: batch?.batch.printedAt,
    dtfPlacements: dtfItems.length,
    screenPrint,
    screenPrintPressed: screenPrint.filter((p) => p.pressed).length,
    screenPrintTotal: screenPrint.length,
    placements,
    pressedCount,
    placementTotal: placements.length,
    fullyPressed: placements.length > 0 && pressedCount === placements.length,
    batchMissing: Boolean(batchId) && !batch,
  };
}

/** Distinct sheets in first-appearance order, with this order's SP progress rolled up per sheet. */
function buildSheets(
  garments: readonly JobCardGarment[],
  batchesById: Map<string, BatchDetail>,
): JobCardSheet[] {
  const sheets: JobCardSheet[] = [];
  const byId = new Map<string, JobCardSheet>();
  for (const g of garments) {
    if (!g.batchId) continue;
    let sheet = byId.get(g.batchId);
    if (!sheet) {
      const bd = batchesById.get(g.batchId);
      sheet = {
        batchId: g.batchId,
        seq: bd?.batch.seq,
        status: bd?.batch.status,
        reissueOf: bd?.batch.reissueOf,
        screenPrintPressed: 0,
        screenPrintTotal: 0,
        loaded: Boolean(bd),
      };
      byId.set(g.batchId, sheet);
      sheets.push(sheet); // first-appearance order preserved
    }
    sheet.screenPrintPressed += g.screenPrintPressed;
    sheet.screenPrintTotal += g.screenPrintTotal;
  }
  return sheets;
}

/**
 * Assemble the job card from an order's detail and the (already-fetched) headers of the sheets its
 * lines point at. `batchesById` may be missing a sheet (a failed fetch) — the join degrades to the
 * line-level state and flags `batchMissing` rather than dropping the garment.
 */
export function buildJobCard(
  detail: OrderDetail,
  batchesById: Map<string, BatchDetail>,
  /**
   * Outstanding reprints for THIS order (`GET /orders/:store/:id/reprints`), or `undefined` while that
   * lookup is in flight or after it failed.
   *
   * ⚠️ `undefined` is the DEFAULT on purpose. The order detail resolves before the reprint lookup, so the
   * honest answer during that window is "don't know" — and a caller who forgets to thread the lookup
   * through gets "not shippable" rather than a false ship-go. Fail safe, not fail green.
   */
  reprints?: readonly ReprintRef[],
): JobCard {
  const reprintChecked = reprints !== undefined;
  const reprintOutstanding = reprints?.length ?? 0;
  const garments = detail.lines.map((line) => toGarment(line, batchesById));
  const { rush, dueDate } = aggregatePriority(detail.lines);
  // Garments with a press checklist (batched + loaded).
  const pressable = garments.filter((g) => g.placementTotal > 0);
  // Real outstanding production that has NO placements to press, so it must NOT be silently ignored
  // by the "all pressed" judgment.
  //
  // ⚠️ This used to test `mappingStatus === 'MAPPED' && UNFULFILLED && OPEN`, which is LANE-BLIND:
  // every cap and ship-only line carries `mappingStatus: 'EXCLUDED'`, so an unproduced cap and an
  // unpacked ship-only line were both invisible to it. Executed against this very function, a card
  // holding a printed-and-pressed DTF line + an unproduced cap + an unpacked ship-only line returned
  // `allGarmentsPressed: true`, contradicting this field's own docblock ("nothing upstream is still
  // owed") — and all six of its tests passed anyway. `garmentOutcome` knows about the lanes.
  const producibleUnpressable = garments.some(
    (g) => garmentOutcome(g) === 'owed' && g.placementTotal === 0,
  );
  // "All pressed" (the nudge to mark-printed) is honest ONLY when the whole order is accounted for:
  // ≥1 pressed garment, every pressable garment fully pressed, AND nothing upstream is still owed —
  // no needs-action line, no un-batched producible line, and no sheet we couldn't load.
  const allGarmentsPressed =
    pressable.length > 0 &&
    pressable.every((g) => g.fullyPressed) &&
    detail.production.needsAction === 0 &&
    !producibleUnpressable &&
    !garments.some((g) => g.batchMissing);
  return {
    order: detail.order,
    production: detail.production,
    garments,
    sheets: buildSheets(garments, batchesById),
    rush,
    dueDate,
    // ⚠️ The verdict is DERIVED FROM THE GARMENTS, not from `production.status` — see `shipVerdict`
    // and `garmentOutcome`. It keeps both D4 properties (an outstanding reprint withholds it; an
    // unanswered lookup withholds it) and adds two the status could not give: a caps-only or
    // ship-only order can now be complete, and a lane line reclassified onto a sheet can no longer
    // be laundered into a ship-go by the rollup's EXCLUDED arm.
    productionComplete:
      shipVerdict({ garments, reprintChecked, reprintOutstanding }).state === 'go',
    reprintOutstanding,
    reprintChecked,
    allGarmentsPressed,
    needsActionCount: detail.production.needsAction,
  };
}

/**
 * Where the job card's back link should go, and what it should say.
 *
 * The job card is reached from TEN screens and only one of them is the Orders queue, but the back link
 * was a hard-coded `<Link to="/orders">Orders</Link>` for all ten. From the production board that is
 * worse than a wrong label: a batched line drops off the open-work queue the moment it is claimed onto
 * a sheet (that is the entire reason `/production` exists), so "back" deposited the operator on a list
 * that could not contain the order they had just been reading.
 *
 * The origin travels in the URL as `?from=…` rather than in history state, for three reasons:
 *   1. It can be LABELLED. `router.history.back()` can only ever render "Back"; on a shop-floor phone
 *      an affordance that names its destination is what makes it trustworthy.
 *   2. It survives a refresh, a bookmark and a pasted link. `useCanGoBack()` is `__TSR_index !== 0`,
 *      so it is false in exactly those cases and would force a static fallback anyway.
 *   3. It is one string in an address, so a deep link keeps working.
 *
 * ⚠️ An unknown or absent value FALLS BACK to Orders — it must never throw. That fallback IS today's
 * behaviour, which is what makes adding `?from=` a zero-regression change: an un-updated caller, an old
 * bookmark and a hand-typed URL all keep behaving exactly as they do now. It is also why the validation
 * lives here rather than in a route-level `validateSearch` (no route in this app uses one, and adding
 * one to a route with ten `Link` callers risks TanStack promoting `search` to a required prop on all
 * ten).
 */
export type OrderBackTarget = {
  to: string;
  /** Only set where the destination needs params of its own (the completed lane). */
  search?: Record<string, string>;
  label: string;
};

const ORDER_BACK_DEFAULT: OrderBackTarget = { to: '/orders', label: 'Orders' };

// `satisfies` rather than a `Record<string, …>` annotation: the annotation would widen the keys to
// `string` and make `OrderBackOrigin` useless for typo-checking a call site.
const ORDER_BACK_TARGETS = {
  production: { to: '/production', label: 'Production' },
  orders: ORDER_BACK_DEFAULT,
  // `Link search` REPLACES the params rather than merging, so the completed lane has to spell its own
  // out — `/orders` alone lands on the OPEN queue, which by definition excludes the order under review.
  'orders-completed': { to: '/orders', search: { view: 'completed' }, label: 'Completed orders' },
  batches: { to: '/batches', label: 'Gang sheets' },
  scan: { to: '/scan', label: 'Scan station' },
  activity: { to: '/activity', label: 'Activity' },
  inventory: { to: '/inventory', label: 'Inventory' },
} satisfies Record<string, OrderBackTarget>;

/** Every origin a caller may pass. Exported so a caller's `from` is typo-checked at the call site. */
export type OrderBackOrigin = keyof typeof ORDER_BACK_TARGETS;

export function orderBackTarget(from: unknown): OrderBackTarget {
  if (typeof from !== 'string') return ORDER_BACK_DEFAULT;
  // ⚠️ `Object.hasOwn`, not `ORDER_BACK_TARGETS[from] ?? DEFAULT`. A bare lookup on an object literal
  // also resolves INHERITED members, so `?from=constructor` (or `toString`, `valueOf`…) would return a
  // truthy non-target that sails straight past the `??` and be rendered as the back link. The URL is
  // user-supplied, so the own-property check is the guard, not a nicety.
  if (!Object.hasOwn(ORDER_BACK_TARGETS, from)) return ORDER_BACK_DEFAULT;
  // Cast justified by the `hasOwn` guard above — it proves membership but does not narrow the type.
  return ORDER_BACK_TARGETS[from as OrderBackOrigin];
}

/**
 * The `search` prop for a `<Link to="/orders/$storeId/$orderId">`, so the job card can name its way
 * back. Exists so the origin is checked AT THE CALL SITE: a bare `search={{ from: 'prodcution' }}`
 * would type-check fine and then silently fall back to Orders, which is precisely the kind of quiet
 * wrong-but-plausible behaviour this whole change is removing.
 */
export function orderCardSearch(from: OrderBackOrigin): { from: OrderBackOrigin } {
  return { from };
}

/**
 * The bin-label identity as one line for the pick moment (bin-labels plan PR-3, § 3.6): what the
 * puller should read off the printed bag label, composed from the SHELF row's own values (the match
 * response's `identity` — never a client-side re-derivation, so the dialog and the label can't
 * disagree). Example: "Gildan 18500 · Black · L · HOODIE — back TX Flag + front LT-logo".
 */
export function stockIdentityLine(identity: {
  blankLabel: string;
  sizeName?: string;
  apparelKind?: string;
  placements: readonly { location: string; designName?: string }[];
}): string {
  const kind =
    identity.apparelKind === 'hoodie' ? 'HOODIE' : identity.apparelKind === 'tshirt' ? 'TEE' : '';
  const head = [identity.blankLabel, identity.sizeName, kind].filter(Boolean).join(' · ');
  const placements = identity.placements
    .map((p) => [p.location, p.designName].filter(Boolean).join(' '))
    .join(' + ');
  return placements ? `${head} — ${placements}` : head;
}
